class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.SRCSET_WIDTHS = [
      165, 360, 533, 720, 940, 1066, 1200, 1400, 1600, 1800, 2000, 2200, 2400,
      2600, 2800, 3000, 3200, 3400, 3600, 3800,
    ];
    this.hasColorSwatches = this.dataset.cardColorSwatches === "true";
    this.hasGalleryScrub = this.dataset.cardGalleryScrub === "true";
    this.initColorSwatches = this.initColorSwatches.bind(this);
    this.initGalleryScrub = this.initGalleryScrub.bind(this);
    this.handleGalleryPointerEnter = this.handleGalleryPointerEnter.bind(this);
    this.handleGalleryPointerMove = this.handleGalleryPointerMove.bind(this);
    this.handleGalleryPointerLeave = this.handleGalleryPointerLeave.bind(this);
    this.galleryActiveIndex = -1;
  }
  connectedCallback() {
    this.hasColorSwatches = this.dataset.cardColorSwatches === "true";
    this.hasGalleryScrub = this.dataset.cardGalleryScrub === "true";
    if (this.hasColorSwatches) {
      this.initColorSwatches();
    }
    if (this.hasGalleryScrub) {
      this.initGalleryScrub();
    }
  }
  initGalleryScrub() {
    if (this.getAttribute("data-gallery-scrub-initialized") === "true") return;
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches)
      return;
    this.cardMedia = this.querySelector(".card-media");
    this.gallerySurface =
      this.querySelector(".card-link-overlay") ||
      this.querySelector(".card__inner") ||
      this.cardMedia;
    this.galleryFrames = Array.from(
      this.querySelectorAll(".media--gallery-frame")
    );
    if (!this.cardMedia || !this.gallerySurface || this.galleryFrames.length < 2)
      return;
    this.setActiveGalleryFrame(0);
    this.gallerySurface.addEventListener(
      "pointerenter",
      this.handleGalleryPointerEnter
    );
    this.gallerySurface.addEventListener(
      "pointermove",
      this.handleGalleryPointerMove
    );
    this.gallerySurface.addEventListener(
      "pointerleave",
      this.handleGalleryPointerLeave
    );
    this.setAttribute("data-gallery-scrub-initialized", "true");
  }
  handleGalleryPointerEnter() {
    this.galleryFrames.slice(1).forEach((frame) => {
      if (frame.dataset.galleryPreloaded === "true") return;
      const preloader = new Image();
      preloader.decoding = "async";
      preloader.loading = "eager";
      preloader.sizes = frame.sizes;
      preloader.srcset = frame.srcset;
      preloader.src = frame.currentSrc || frame.src;
      frame.dataset.galleryPreloaded = "true";
    });
  }
  handleGalleryPointerMove(event) {
    if (!this.cardMedia || !this.galleryFrames?.length) return;
    if (event.pointerType && event.pointerType !== "mouse") return;
    const bounds = this.cardMedia.getBoundingClientRect();
    if (!bounds.width) return;
    const progress = Math.min(
      1,
      Math.max(0, (event.clientX - bounds.left) / bounds.width)
    );
    const maxIndex = this.galleryFrames.length - 1;
    const nextIndex = Math.min(maxIndex, Math.floor(progress * (maxIndex + 1)));
    this.setActiveGalleryFrame(nextIndex);
  }
  handleGalleryPointerLeave() {
    this.setActiveGalleryFrame(0);
  }
  setActiveGalleryFrame(nextIndex) {
    if (!this.galleryFrames?.length) return;
    if (nextIndex === this.galleryActiveIndex) return;
    this.galleryFrames[this.galleryActiveIndex]?.classList.remove("is-active");
    this.galleryFrames[nextIndex]?.classList.add("is-active");
    this.galleryActiveIndex = nextIndex;
  }
  initColorSwatches() {
    if (this.getAttribute("data-swatch-initialized") === "true") return;
    const allInputs = this.querySelectorAll(".js-color-swatches input");
    allInputs.forEach((input) => {
      input.addEventListener("click", (event) => {
        if (event.currentTarget.checked) {
          const fallbackVariantId = event.currentTarget.dataset?.variantId;
          this.handleSwatchChange(fallbackVariantId);
          this.updateCardInputAttr(allInputs, event.currentTarget);
        }
      });
    });
    this.setAttribute("data-swatch-initialized", "true");
  }
  updateCardInputAttr(allInputs, currentInput) {
    if (!currentInput || !allInputs || allInputs.length === 0) return;
    allInputs.forEach((input) => {
      input.removeAttribute("checked");
    });
    currentInput.setAttribute("checked", true);
  }
  handleSwatchChange(fallbackVariantId) {
    const productVariantsJson = this.querySelector(
      "[data-product-card-variants]"
    );
    if (!productVariantsJson) return;
    const productVariants = JSON.parse(productVariantsJson.textContent);
    const selectedInput = this.querySelector(
      ".js-color-swatches input:checked"
    );
    if (!selectedInput) return;
    const selectedValue = selectedInput.value;
    const matchedVariant = productVariants.find((variant) => {
      return variant.options.includes(selectedValue);
    });
    const variantId = matchedVariant ? matchedVariant.id : fallbackVariantId;
    this.updateLinkOverlay(variantId);
    this.enableAddToCart();
    this.updateAddToCartButton(variantId);
    const productFeaturedMediaJson = this.querySelector(
      "[data-product-card-feat-media]"
    );
    const productFeaturedMedia = JSON.parse(
      productFeaturedMediaJson.textContent
    );
    const featuredMedia =
      matchedVariant.featured_media?.preview_image ||
      matchedVariant.featured_image ||
      productFeaturedMedia?.preview_image ||
      productFeaturedMedia;
    this.updateImageElement(featuredMedia);
  }
  generateSrcset(src, maxW) {
    const url = new URL(src, window.location.origin);
    return this.SRCSET_WIDTHS.filter((w) => w <= maxW)
      .map((w) => {
        url.searchParams.set("width", w);
        return `${url.href} ${w}w`;
      })
      .join(", ");
  }
  generateSizes() {
    const ratio =
      parseFloat(this.dataset.productRatio || this.dataset.productImgRatio) || 1;
    const fit = this.dataset.productFit || this.dataset.productImgFit || "cover";
    const cols = Number(this.dataset.productColumns) || 1;
    const imgAspect = parseFloat(this.dataset.originalAspectRatio) || ratio;
    let calcRatio = 1;
    if (fit === "cover" && imgAspect > ratio) {
      calcRatio = +(imgAspect / ratio).toFixed(2);
    }
    let sizes = `(min-width: 1200px) calc(${calcRatio} * (100vw / ${cols}))`;
    if (cols >= 3) {
      sizes += `, (min-width: 990px) calc(${calcRatio} * (100vw / 3))`;
    }
    if (cols >= 2) {
      sizes += `, (min-width: 576px) calc(${calcRatio} * (100vw / 2))`;
    }
    const mobileSize = `calc(${calcRatio} * 100vw)`;
    sizes += `, ${mobileSize}`;
    return sizes;
  }
  normalizeImageUrl(src) {
    const url = new URL(src, window.location.origin);
    const version = url.searchParams.get("v");
    return url.host + url.pathname + (version ? `?v=${version}` : "");
  }
  updateImageElement(featuredMedia) {
    const primaryImage = this.querySelector(".media--first");
    if (!featuredMedia || !primaryImage) return;
    if (this.hasGalleryScrub) {
      const newSrcset = this.generateSrcset(
        featuredMedia.src,
        featuredMedia.width
      );
      const sizes = this.generateSizes();
      primaryImage.src = featuredMedia.src;
      primaryImage.srcset = newSrcset;
      primaryImage.sizes = sizes;
      primaryImage.alt = featuredMedia.alt || this.dataset.productTitle;
      this.querySelectorAll(".media--gallery-frame").forEach((frame, index) => {
        if (index === 0) return;
        frame.src = featuredMedia.src;
        frame.srcset = newSrcset;
        frame.sizes = sizes;
        frame.alt = "";
        frame.dataset.galleryPreloaded = "true";
      });
      this.setActiveGalleryFrame(0);
      return;
    }
    const secondaryImage = this.querySelector(".media--second");
    const newSrc = featuredMedia.src;
    const newSrcset = this.generateSrcset(
      featuredMedia.src,
      featuredMedia.width
    );
    const sizes = this.generateSizes();
    const altText = featuredMedia.alt || this.dataset.productTitle;
    if (
      this.normalizeImageUrl(primaryImage.src) ===
      this.normalizeImageUrl(newSrc)
    ) {
      if (secondaryImage) {
        const priPath = new URL(primaryImage.src).pathname;
        const secPath = new URL(secondaryImage.src).pathname;
        if (priPath === secPath) {
          primaryImage.remove();
          secondaryImage.classList.replace("media--second", "media--first");
          return;
        }
      }
      return;
    }
    primaryImage.style.transition = "opacity 250ms ease-in-out";
    primaryImage.style.opacity = "0";
    if (secondaryImage) {
      secondaryImage.style.transition = "opacity 250ms ease-in-out";
      secondaryImage.style.opacity = "0";
    }
    const onFadeOut = (e) => {
      if (e.propertyName !== "opacity") return;
      primaryImage.removeEventListener("transitionend", onFadeOut);
      primaryImage.onload = () => {
        primaryImage.style.transition = "opacity 250ms ease-in-out";
        primaryImage.style.opacity = "1";
        primaryImage.onload = null;
        primaryImage.style.transition = "";
      };
      primaryImage.src = newSrc;
      primaryImage.srcset = newSrcset;
      primaryImage.sizes = sizes;
      primaryImage.alt = altText;
      if (secondaryImage) secondaryImage.remove();
    };
    primaryImage.addEventListener("transitionend", onFadeOut);
  }
  enableAddToCart() {
    const btn = this.querySelector('.card__add-to-cart button[name="add"]');
    if (!btn) return;
    const label = btn.querySelector("[data-add-to-cart-label]");
    const sold = btn.querySelector("[data-sold-out-label]");
    const errMsg = this.querySelector(".product-form__error-message-wrapper");
    btn.setAttribute("aria-disabled", "false");
    label?.classList.remove("hidden");
    sold?.classList.add("hidden");
    errMsg?.setAttribute("hidden", "");
  }
  updateAddToCartButton(variantId) {
    const btn = this.querySelector('.card__add-to-cart input[name="id"]');
    if (!btn) return;
    btn.value = variantId;
  }
  updateLinkOverlay(variantId) {
    if (!variantId) return;
    const linkOverlay = this.querySelector("[data-card-swatch-link]");
    if (!linkOverlay) return;
    const currentHref = linkOverlay.getAttribute("href");
    const newUrl = new URL(currentHref, window.location.origin);
    newUrl.searchParams.set("variant", variantId);
    linkOverlay.setAttribute("href", newUrl.toString());
  }
}
if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}
class ProductCardSmall extends ProductCard {
  constructor() {
    super();
  }
  generateSizes() {
    const ratio = parseFloat(this.dataset.productRatio) || 1;
    const fit = this.dataset.productFit || "cover";
    const cols = Number(this.dataset.productColumns) || 1;
    const imgAspect = parseFloat(this.dataset.originalAspectRatio) || ratio;
    let calcRatio = 1;
    if (fit === "cover" && imgAspect > ratio) {
      calcRatio = +(imgAspect / ratio).toFixed(2);
    }
    let sizes = `(min-width: 1200px) calc(${calcRatio} * (100vw / ${cols}))`;
    if (cols >= 3) {
      sizes += `, (min-width: 990px) calc(${calcRatio} * (100vw / 3))`;
    }
    if (cols >= 2) {
      sizes += `, (min-width: 576px) calc(${calcRatio} * (100vw / 2))`;
    }
    const mobileSize = `calc(${calcRatio} * 100vw)`;
    sizes += `, ${mobileSize}`;
    return sizes;
  }
}
if (!customElements.get("product-card-small")) {
  customElements.define("product-card-small", ProductCardSmall);
}
